import { FilterCompTypes } from 'app/ComponentLibrary/Filter/utils';
import { calculateTreeCount } from '@AMIEWEB/Common/TreeView/TreeViewHelpers';
import type { IRecruiterOption } from '../MyCandidates.redux';

export enum recCandidatesFilterName {
  placementRecruiter = 'placementRecruiter',
  discipline = 'discipline',
  specialty = 'specialty',
  states = 'states',
  candidateStatus = 'candidateStatus',
  rebooked = 'rebooked',
  bookedOutOfRange = 'bookedOutOfRange',
  placementStatus = 'placementStatus',
}

export interface ICandidatesFilter {
  placementRecruiter: IRecruiterOption | IRecruiterOption[] | null;
  discipline: { id: number; name: string; value: string }[];
  specialty: { id: number; name: string; value: string }[];
  states: string;
  candidateStatus: { id: number; name: string; value: string }[];
  rebooked: string;
  bookedOutOfRange: string;
  placementStatus: any[]; // Tree nodes from TREE filter (returnsObjectAsValue)
}

export const defaultValues: ICandidatesFilter = {
  placementRecruiter: null,
  discipline: [],
  specialty: [],
  states: 'all',
  candidateStatus: [],
  rebooked: 'all',
  bookedOutOfRange: 'all',
  placementStatus: [],
};

export interface ICandidatesFilterSpecs {
  disciplineOptions: { id: number; name: string; value: string }[];
  specialtyOptions: { id: number; name: string; value: string }[];
  stateOptions: { id: number; name: string; value: string }[];
  candidateStatusOptions: { id: number; name: string; value: string }[];
  placementStatusTreeOptions: any[]; // From getPlacementStatusTreeOptions()
  rebookedCount: number;
  notRebookedCount: number;
  bookedOutOfRangeCount: number;
  bookedInRangeCount: number;
}

export const getFilterSpecs = (
  recruiterOptions: IRecruiterOption[],
  filterSpecs: ICandidatesFilterSpecs,
): any[] => [
  {
    filterName: 'Placement Recruiter',
    name: recCandidatesFilterName.placementRecruiter,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: recruiterOptions,
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Discipline',
    name: recCandidatesFilterName.discipline,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: true,
    options:
      filterSpecs.disciplineOptions.length > 0
        ? filterSpecs.disciplineOptions
        : [{ id: 0, name: 'All Disciplines (0)', value: 'All' }],
    selectAllValue: 'All',
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Specialty',
    name: recCandidatesFilterName.specialty,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: true,
    options:
      filterSpecs.specialtyOptions.length > 0
        ? filterSpecs.specialtyOptions
        : [{ id: 0, name: 'All Specialties (0)', value: 'All' }],
    selectAllValue: 'All',
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'States',
    name: recCandidatesFilterName.states,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options:
      filterSpecs.stateOptions.length > 0
        ? filterSpecs.stateOptions
        : [{ id: 0, name: 'All States (0)', value: 'all' }],
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Candidate Status',
    name: recCandidatesFilterName.candidateStatus,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: true,
    options:
      filterSpecs.candidateStatusOptions.length > 0
        ? filterSpecs.candidateStatusOptions
        : [{ id: 0, name: 'All (0)', value: 'All' }],
    selectAllValue: 'All',
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Rebooked',
    name: recCandidatesFilterName.rebooked,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: [
      { id: 0, name: `All (${filterSpecs.rebookedCount + filterSpecs.notRebookedCount})`, value: 'all' },
      { id: 1, name: `Yes (${filterSpecs.rebookedCount})`, value: 'Y' },
      { id: 2, name: `No (${filterSpecs.notRebookedCount})`, value: 'N' },
    ],
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Booked out of range',
    name: recCandidatesFilterName.bookedOutOfRange,
    type: FilterCompTypes.TYPEAHEADDROPDOWN,
    isMultiSelect: false,
    options: [
      {
        id: 0,
        name: `All (${filterSpecs.bookedOutOfRangeCount + filterSpecs.bookedInRangeCount})`,
        value: 'all',
      },
      { id: 1, name: `Yes (${filterSpecs.bookedOutOfRangeCount})`, value: 'Y' },
      { id: 2, name: `No (${filterSpecs.bookedInRangeCount})`, value: 'N' },
    ],
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
  },
  {
    filterName: 'Placement Status',
    name: recCandidatesFilterName.placementStatus,
    type: FilterCompTypes.TREE,
    isMultiSelect: true,
    options: filterSpecs.placementStatusTreeOptions,
    placeholder: 'Select',
    version2: true,
    returnsObjectAsValue: true,
    size: 'large',
    customSelectedCount: value => calculateTreeCount(value),
    fallbackPlacements: ['top-start', 'bottom-start', 'right'],
    applyOnClickAway: true,
    isSelectAll: true,
  },
];
